import { indexedDBService } from "@/services/storage";
import { logger } from "@/utils/debug";

export interface GroupsConfig {
  groupColumnName: string | null;
  visibleGroups: string[];
  groupConfigs: Record<
    string,
    {
      name?: string;
      color?: string;
      shape?: "circle" | "square" | "triangle" | "star" | "diamond";
    }
  >;
  layerConfig?: { color?: string; shape?: "circle" | "square" | "triangle" | "star" | "diamond" };
}

/**
 * Saves groups configuration to IndexedDB
 * @param workspaceId - Workspace identifier
 * @param getDatasetId - Function to get dataset ID from workspace ID
 * @param getGroupState - Function to get current group state
 * @param context - Optional context string for logging (e.g., "setGroupColumnName")
 */
export async function saveGroupsConfigToIndexedDB(
  workspaceId: string,
  getDatasetId: (workspaceId: string) => string | null,
  getGroupState: (workspaceId: string) => GroupsConfig | undefined,
  context?: string
): Promise<void> {
  const datasetId = getDatasetId(workspaceId);
  if (!datasetId) {
    logger.warn(
      `[MapStore] Cannot save groups config${context ? ` (${context})` : ""}: datasetId is null for workspace`,
      workspaceId
    );
    return;
  }

  const groupState = getGroupState(workspaceId);
  if (!groupState) {
    logger.warn(
      `[MapStore] Cannot save groups config${context ? ` (${context})` : ""}: groupState is null for workspace`,
      workspaceId
    );
    return;
  }

  try {
    await indexedDBService.storeGroupsConfig(datasetId, {
      groupColumnName: groupState.groupColumnName,
      visibleGroups: groupState.visibleGroups,
      groupConfigs: groupState.groupConfigs,
      layerConfig: groupState.layerConfig,
    });
    logger.log(
      `[MapStore] Successfully saved groups config${context ? ` (${context})` : ""} for dataset`,
      datasetId
    );
  } catch (error) {
    logger.error(
      `[MapStore] Failed to save groups config${context ? ` (${context})` : ""}:`,
      error
    );
  }
}

